import { supabase } from '../supabase';

export type SyncDirection = 'import_only' | 'bidirectional';

/** Config di import persistita per-connessione (letta anche dal cron alfadocs-sync-cron). */
export interface AlfaDocsImportConfig {
  import_as: 'lead' | 'client';
  assigned_to_user_id: string | null;
  pipeline_id: string | null;
  pipeline_stage_id: string | null;
  client_state_id: string | null;
}

/** Esito del controllo pazienti eliminati (supabase/functions/_shared/alfadocsDeletions.ts) */
export interface AlfaDocsDeletionSummary {
  candidates: number;
  removed: number;
  state_changed: number;
  pending: number;
  restored: number;
  skipped_reason?: string;
}

export interface AlfaDocsConfig {
  id: string;
  organization_id: string;
  api_key: string;
  is_active: boolean;
  display_name: string | null;
  configuration: {
    practice_id?: string;
    archive_id?: string;
    practice_name?: string;
    sync_direction?: SyncDirection;
    appointments_config?: any;
    auto_sync_enabled?: boolean;
    /** Sync pazienti automatica: ogni notte (default) oppure ogni ora */
    patient_sync_frequency?: 'daily' | 'hourly';
    /** Stato in cui spostare i contatti il cui paziente è stato eliminato su AlfaDocs (null = solo segnalazione) */
    deleted_patient_state_id?: string | null;
    import_config?: AlfaDocsImportConfig;
    last_sync?: {
      patients?: string;
      appointments?: string;
      status?: string;
      synced_count?: number;
      errors?: string[];
      deletions?: AlfaDocsDeletionSummary;
    };
  };
  created_at?: string;
  updated_at?: string;
}

interface TestConnectionResult {
  success: boolean;
  message?: string;
  practiceId?: string;
  archiveId?: string;
  practiceName?: string;
}

export class AlfaDocsService {
  /**
   * List all AlfaDocs configurations (connections) for an organization, ordered by creation.
   */
  static async listConfigs(organizationId: string): Promise<AlfaDocsConfig[]> {
    const { data, error } = await supabase
      .from('api_configurations')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('service_name', 'alfadocs')
      .order('created_at', { ascending: true });
    if (error || !data) return [];
    return data.map((d: any) => ({
      id: d.id,
      organization_id: d.organization_id,
      api_key: d.api_key,
      is_active: d.is_active,
      display_name: d.display_name || d.configuration?.practice_name || 'Studio',
      configuration: d.configuration || {},
      created_at: d.created_at,
      updated_at: d.updated_at,
    }));
  }

  /**
   * Return the ACTIVE AlfaDocs connection with the oldest created_at for an organization.
   */
  static async getOldestActiveConfig(organizationId: string): Promise<AlfaDocsConfig | null> {
    const configs = await this.listConfigs(organizationId);
    return configs.find((c) => c.is_active) || null;
  }

  /**
   * LEGACY: ritorna la connessione ATTIVA PIÙ VECCHIA dell'org.
   * Con una sola connessione è "la" connessione (comportamento storico).
   * Per operazioni su uno studio specifico usare listConfigs + configId.
   */
  static async getConfig(organizationId: string): Promise<AlfaDocsConfig | null> {
    return this.getOldestActiveConfig(organizationId);
  }

  /**
   * Test connection with AlfaDocs API using the provided API key.
   * Calls the alfadocs-proxy edge function with action 'verify'.
   */
  static async testConnection(organizationId: string, apiKey: string): Promise<TestConnectionResult> {
    try {
      const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
        body: {
          action: 'verify',
          apiKey,
        },
      });

      if (error) {
        return { success: false, message: error.message };
      }

      if (!data?.success) {
        return { success: false, message: data?.error || 'Verifica fallita' };
      }

      return {
        success: true,
        practiceId: data.practiceId,
        archiveId: data.archiveId,
        practiceName: data.practiceName,
      };
    } catch (error) {
      return { success: false, message: (error as Error).message };
    }
  }

  /**
   * Save AlfaDocs configuration (create a new connection, or update an existing one via opts.configId).
   * Blocca il collegamento duplicato dello stesso studio (practice_id) all'interno della stessa org.
   */
  static async saveConfig(
    organizationId: string,
    apiKey: string,
    practiceId: string,
    archiveId: string,
    practiceName?: string,
    opts?: { configId?: string; displayName?: string }
  ): Promise<AlfaDocsConfig | null> {
    try {
      const all = await this.listConfigs(organizationId);
      const duplicate = all.find((c) => c.configuration?.practice_id === practiceId && c.id !== opts?.configId);
      if (duplicate) throw new Error('Questo studio AlfaDocs è già collegato');

      const displayName = opts?.displayName || practiceName || 'Studio';
      const configuration = { practice_id: practiceId, archive_id: archiveId, practice_name: practiceName || '' };

      if (opts?.configId) {
        const existing = all.find((c) => c.id === opts.configId);
        const { error } = await supabase
          .from('api_configurations')
          .update({
            api_key: apiKey,
            display_name: displayName,
            configuration: { ...(existing?.configuration || {}), ...configuration },
            updated_at: new Date().toISOString(),
          })
          .eq('id', opts.configId);
        if (error) throw error;
      } else {
        const isFirst = all.length === 0;
        const { error } = await supabase.from('api_configurations').insert({
          organization_id: organizationId,
          service_name: 'alfadocs',
          api_key: apiKey,
          display_name: displayName,
          configuration,
          is_active: isFirst,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        if (error) throw error;
      }

      const updated = await this.listConfigs(organizationId);
      return updated.find((c) => c.configuration?.practice_id === practiceId) || null;
    } catch (error) {
      console.error('Error saving AlfaDocs config:', error);
      throw error;
    }
  }

  /**
   * Persiste la configurazione di import (pipeline/fase/stato/assegnatario) sulla
   * connessione, così il cron di auto-sync applica le stesse impostazioni del
   * sync manuale. Read-merge-update su configuration (pattern AutoSyncCard).
   */
  static async saveImportConfig(configId: string, importConfig: AlfaDocsImportConfig): Promise<void> {
    const { data: existing, error: readError } = await supabase
      .from('api_configurations')
      .select('configuration')
      .eq('id', configId)
      .single();
    if (readError || !existing) throw new Error(readError?.message || 'Connessione AlfaDocs non trovata');
    const { error } = await supabase
      .from('api_configurations')
      .update({
        configuration: { ...existing.configuration, import_config: importConfig },
        updated_at: new Date().toISOString(),
      })
      .eq('id', configId);
    if (error) throw new Error(error.message);
  }

  /**
   * Disconnect AlfaDocs (deactivate config)
   */
  static async disconnect(configId: string): Promise<void> {
    const { error } = await supabase
      .from('api_configurations')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', configId);
    if (error) throw error;
  }

  /**
   * Fetch patients via proxy edge function
   */
  static async fetchPatients(organizationId: string, params?: { limit?: number; offset?: number }, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'patients', params, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch appointments via proxy edge function
   */
  static async fetchAppointments(organizationId: string, params?: { dateStart?: string; dateEnd?: string }, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'appointments', params, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch bills via proxy edge function
   */
  static async fetchBills(organizationId: string, params?: { dateStart?: string; dateEnd?: string; carePlanId?: string | number }, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'bills', params, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch care plans for a specific patient
   */
  static async fetchPatientCarePlans(organizationId: string, patientId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'patient-care-plans', params: { patientId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch care plan detail (includes clinical notes in 'text' field)
   */
  static async fetchCarePlanDetail(organizationId: string, carePlanId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'care-plan-detail', params: { carePlanId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch entries (procedures) for a specific appointment
   */
  static async fetchAppointmentEntries(organizationId: string, appointmentId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'appointment-entries', params: { appointmentId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch documents for a specific patient
   */
  static async fetchPatientDocuments(organizationId: string, patientId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'patient-documents', params: { patientId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Scarica il contenuto di un documento (base64 + contentType) per aprirlo/scaricarlo
   */
  static async fetchDocumentContent(organizationId: string, patientId: string, documentId: string | number, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'document-content', params: { patientId, documentId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data as { success: boolean; base64?: string; contentType?: string; error?: string };
  }

  /**
   * Fetch operators (dentists, hygienists, etc.)
   */
  static async fetchOperators(organizationId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'operators', configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch free appointment slots
   */
  static async fetchFreeSlots(organizationId: string, startDate: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'free-slots', params: { startDate }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Trigger patient sync (single call, no progress)
   */
  static async syncPatients(organizationId: string, assignedToUserId?: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-sync', {
      body: { organizationId, assigned_to_user_id: assignedToUserId, config_id: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Sync patients with real progress — splits into batches
   */
  static async syncPatientsWithProgress(
    organizationId: string,
    assignedToUserId?: string,
    onProgress?: (current: number, total: number, message: string) => void,
    cancelledRef?: { current: boolean },
    importConfig?: {
      importAs?: 'lead' | 'client';
      stateId?: string;
      pipelineId?: string;
      pipelineStageId?: string;
    },
    opts?: { configId?: string }
  ) {
    const BATCH_SIZE = 100;

    // Step 1: Get total count via proxy
    onProgress?.(0, 0, 'Connessione ad AlfaDocs...');
    const countRes = await AlfaDocsService.fetchPatients(organizationId, { limit: 10, offset: 0 }, opts);
    const totalPatients = countRes?.count ?? 0;

    if (totalPatients === 0) {
      return { success: true, total_fetched: 0, created: 0, updated: 0, skipped: 0, errors: [] as string[], deletions: null as AlfaDocsDeletionSummary | null, cancelled: false };
    }

    const totalBatches = Math.ceil(totalPatients / BATCH_SIZE);
    const accumulated = { created: 0, updated: 0, skipped: 0, total_fetched: 0, errors: [] as string[], deletions: null as AlfaDocsDeletionSummary | null };

    // Step 2: Process in batches
    for (let i = 0; i < totalBatches; i++) {
      // Check cancellation before each batch
      if (cancelledRef?.current) {
        return { success: true, ...accumulated, cancelled: true };
      }

      const offset = i * BATCH_SIZE;
      const batchEnd = Math.min(offset + BATCH_SIZE, totalPatients);
      const isLast = i === totalBatches - 1;

      onProgress?.(
        i + 1,
        totalBatches,
        `Sincronizzazione pazienti ${offset + 1}–${batchEnd} di ${totalPatients}...`
      );

      const { data, error } = await supabase.functions.invoke('alfadocs-sync', {
        body: {
          organizationId,
          assigned_to_user_id: assignedToUserId,
          batch_offset: offset,
          batch_limit: BATCH_SIZE,
          total_hint: totalPatients,
          save_sync_result: isLast,
          import_as: importConfig?.importAs || 'lead',
          client_state_id: importConfig?.stateId,
          pipeline_id: importConfig?.pipelineId,
          pipeline_stage_id: importConfig?.pipelineStageId,
          config_id: opts?.configId,
        },
      });

      if (error) throw error;

      accumulated.created += data?.created || 0;
      accumulated.updated += data?.updated || 0;
      accumulated.skipped += data?.skipped || 0;
      accumulated.total_fetched += data?.total_fetched || 0;
      if (data?.errors) accumulated.errors.push(...data.errors);
    }

    // I blocchi vedono 100 pazienti alla volta: i pazienti eliminati su AlfaDocs si
    // riconoscono solo rileggendo l'elenco completo, in un passaggio finale dedicato.
    if (cancelledRef?.current) {
      return { success: true, ...accumulated, cancelled: true };
    }
    onProgress?.(totalBatches, totalBatches, 'Controllo pazienti eliminati su AlfaDocs...');
    const { data: deletionData, error: deletionError } = await supabase.functions.invoke('alfadocs-sync', {
      body: { organizationId, mode: 'deletions', config_id: opts?.configId },
    });
    if (deletionError) {
      accumulated.errors.push(`Controllo pazienti eliminati non riuscito: ${deletionError.message}`);
    }
    accumulated.deletions = (deletionData?.deletions || null) as AlfaDocsDeletionSummary | null;

    return { success: true, ...accumulated, cancelled: false };
  }

  /**
   * Preview bidirectional push — shows what would be created/linked without doing anything
   */
  static async syncPatientsPreview(organizationId: string, filterTag?: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-sync', {
      body: { organizationId, bidirectional_mode: 'preview', bidirectional_filter_tag: filterTag || undefined, config_id: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Confirm bidirectional push — push only selected clients
   */
  static async syncPatientsPush(organizationId: string, clientIds: string[], opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-sync', {
      body: { organizationId, bidirectional_mode: 'push', bidirectional_client_ids: clientIds, config_id: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /** Backfill legacy→multi (chunk). Ripetere finché remaining_clients+remaining_events === 0. */
  static async runBackfillChunk(organizationId: string) {
    const { data, error } = await supabase.functions.invoke('alfadocs-sync', {
      body: { organizationId, mode: 'backfill' },
    });
    if (error) throw error;
    return data as {
      success: boolean;
      converted_clients: number;
      converted_events: number;
      remaining_clients: number;
      remaining_events: number;
      conversion_errors?: string[];
    };
  }

  /**
   * Create a patient in AlfaDocs
   */
  static async createPatient(organizationId: string, patient: {
    firstName: string;
    lastName: string;
    email?: string;
    italianFiscalCode?: string;
    phone?: string;
    street?: string;
    city?: string;
    postcode?: string;
    province?: string;
    dateBirth?: string;
    gender?: string;
  }, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'create-patient', params: patient, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Update a patient in AlfaDocs
   */
  static async updatePatient(organizationId: string, patientId: string, fields: Record<string, any>, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'update-patient', params: { patientId, ...fields }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Delete a patient in AlfaDocs
   */
  static async deletePatient(organizationId: string, patientId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'delete-patient', params: { patientId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch phone numbers for a specific patient
   */
  static async fetchPatientPhoneNumbers(organizationId: string, patientId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'patient-phone-numbers', params: { patientId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch a single appointment detail
   */
  static async fetchAppointment(organizationId: string, appointmentId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'get-appointment', params: { appointmentId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Create an appointment in AlfaDocs
   * Required fields: date, patientId, duration, colorId
   * Optional: emailReminder, smsReminder, description, type (telehealth/inPractice), allDay, state, chairId, operatorId
   */
  static async createAppointment(organizationId: string, appointment: {
    date: string;
    patientId: number;
    duration: number;
    colorId: number;
    emailReminder?: boolean;
    smsReminder?: boolean;
    description?: string;
    type?: 'telehealth' | 'inPractice';
    allDay?: boolean;
    state?: string;
    chairId?: string;
    operatorId?: number;
  }, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'create-appointment', params: appointment, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Update an appointment in AlfaDocs
   */
  static async updateAppointment(organizationId: string, appointmentId: string, fields: Record<string, any>, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'update-appointment', params: { appointmentId, ...fields }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch a single bill detail
   */
  static async fetchBillDetail(organizationId: string, billId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'bill-detail', params: { billId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch liabilities for a specific bill
   */
  static async fetchBillLiabilities(organizationId: string, billId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'bill-liabilities', params: { billId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Fetch entries (procedures) for a specific care plan
   */
  static async fetchCarePlanEntries(organizationId: string, carePlanId: string, opts?: { configId?: string }) {
    const { data, error } = await supabase.functions.invoke('alfadocs-proxy', {
      body: { action: 'care-plan-entries', params: { carePlanId }, configId: opts?.configId },
    });
    if (error) throw error;
    return data;
  }

  /**
   * Update sync direction setting
   */
  static async updateSyncDirection(configId: string, direction: SyncDirection): Promise<void> {
    const existing = await supabase
      .from('api_configurations')
      .select('configuration')
      .eq('id', configId)
      .single();

    if (existing.error) throw existing.error;

    const { error } = await supabase
      .from('api_configurations')
      .update({
        configuration: {
          ...existing.data.configuration,
          sync_direction: direction,
        },
        updated_at: new Date().toISOString(),
      })
      .eq('id', configId);
    if (error) throw error;
  }
}
